import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { navigate } from '../core/router';
import {
  diskLevel,
  emmcLevel,
  hex2,
  levelColor,
  levelFill,
  shortDuration,
  tempLevel,
} from '../core/format';
import type { AgcLevel, Device, DeviceCommand, ScriptInfo } from '../models/device';
import type { BrandProfile } from '../models/brand';

const AGC_LEVELS: Array<{ key: AgcLevel; label: string; hint: string }> = [
  { key: 'off', label: 'Kapalı', hint: 'ham ses' },
  { key: 'low', label: 'Düşük', hint: '6:1 · -18dB · +9dB' },
  { key: 'medium', label: 'Orta', hint: '10:1 · -24dB · +15dB' },
  { key: 'high', label: 'Yüksek', hint: '20:1 · -30dB · +24dB (saha)' },
];
import { devices } from '../services/device-service';
import { brands as brandService } from '../services/brand-service';
import { settings, thresholdNumbers } from '../services/settings-service';
import { session } from '../services/auth-service';
import { ssh } from '../services/ssh-service';
import { showToast } from '../services/toast-service';
import { confirmDialog } from '../services/confirm-service';
import { pendingBadge, rescueBadge, wgModeBadge } from '../components/rescue-badge';

/** Detay otomatik yenileme (kurtarma ilerlemesi/komut sonucu canlı görünsün) */
const AUTO_REFRESH_MS = 10_000;

/** Cihaz detayı: kimlik, sağlık, imaj profili, işlemler + gömülü web-SSH. */
export class DeviceDetailView extends SmgElement {
  private device: Device | null = null;
  private brandList: BrandProfile[] = [];
  private loading = true;
  private error = '';

  private nameDraft = '';
  private deviceIdDraft = '';
  private businessSerialDraft = '';
  private sshOpen = false;
  private commandsList: DeviceCommand[] = [];
  private scripts: ScriptInfo[] = [];
  private scriptName = '';
  private scriptArgs = '';
  private expandedCmd: number | null = null;
  private assignOpen = false;
  private assignProfile = '';
  private forceStage: 'confirm' | 'sent' | null = null;
  private forceText = '';
  private deleteStage: 'confirm' | null = null;
  private deleteText = '';

  private refreshTimer: ReturnType<typeof setInterval> | undefined;

  private get serial(): string {
    return this.dataset['serial'] ?? '';
  }

  protected override onConnected(): void {
    this.wireActs();
    // Sürüklenebilir SSH paneli kapat düğmesi
    this.addEventListener('smg-close', () => {
      this.sshOpen = false;
      this.render();
    });
    void this.load();
    this.refreshTimer = setInterval(() => {
      // Diyalog/SSH açıkken ya da kullanıcı kimlik alanını düzenlerken dokunma
      if (this.assignOpen || this.forceStage || this.deleteStage || this.sshOpen) return;
      const active = document.activeElement as HTMLElement | null;
      if (active && this.contains(active) && active.tagName === 'INPUT') return;
      void this.load(true);
    }, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private onVisible = () => {
    if (document.visibilityState === 'visible' && !this.assignOpen && !this.forceStage && !this.deleteStage) {
      void this.load(true);
    }
  };

  protected override onDisconnected(): void {
    clearInterval(this.refreshTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  /**
   * SSH açıkken yeniden çizim, canlı terminal iframe'ini KOPARMASIN diye
   * mevcut <smg-ssh-panel> elementini ayırıp yeni ağaca geri takar.
   */
  protected override render(): void {
    const live = this.sshOpen ? this.qs<HTMLElement>('smg-ssh-panel') : null;
    if (live) live.remove();
    super.render();
    if (live) {
      const placeholder = this.qs<HTMLElement>('smg-ssh-panel');
      if (placeholder) placeholder.replaceWith(live);
      else this.qs('.scr')?.appendChild(live);
    }
  }

  private async load(silent = false): Promise<void> {
    if (!silent) {
      this.loading = true;
      this.error = '';
      this.render();
    }
    try {
      const [device, brandList, cmds, scripts] = await Promise.all([
        devices.get(this.serial),
        brandService.list(),
        devices.commands(this.serial).catch(() => [] as DeviceCommand[]),
        this.scripts.length ? Promise.resolve(this.scripts) : devices.scripts().catch(() => [] as ScriptInfo[]),
        settings.ensureLoaded(),
      ]);
      this.device = device;
      this.brandList = brandList;
      this.commandsList = cmds;
      this.scripts = scripts;
      if (!this.scriptName && scripts.length) this.scriptName = scripts[0]!.name;
      if (!silent) {
        this.nameDraft = device.name;
        this.deviceIdDraft = device.deviceId;
        this.businessSerialDraft = device.businessSerial ?? '';
      }
      this.error = '';
    } catch {
      if (!silent) this.error = 'Cihaz bilgisi alınamadı.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private wireActs(): void {
    this.onAct('click', {
      back: () => navigate('#/devices'),
      saveIdentity: () => void this.saveIdentity(),
      openSSH: () => {
        this.sshOpen = true;
        this.render();
      },
      closeSSH: () => {
        this.sshOpen = false;
        this.render();
      },
      restart: () => void this.confirmAndSend('restart'),
      rescue: () => void this.confirmAndSend('rescue'),
      openForce: () => {
        this.forceStage = 'confirm';
        this.forceText = '';
        this.render();
      },
      closeForce: () => {
        this.forceStage = null;
        this.render();
      },
      confirmForce: () => void this.confirmForce(),
      openDelete: () => {
        this.deleteStage = 'confirm';
        this.deleteText = '';
        this.render();
      },
      closeDelete: () => {
        this.deleteStage = null;
        this.render();
      },
      confirmDelete: () => void this.confirmDelete(),
      openAssign: () => {
        this.assignOpen = true;
        this.assignProfile = this.device?.brand ?? this.brandList[0]?.name ?? '';
        this.render();
      },
      closeAssign: () => {
        this.assignOpen = false;
        this.render();
      },
      pickAssign: (el) => {
        this.assignProfile = el.dataset['brand'] ?? '';
        this.render();
      },
      confirmAssign: () => void this.confirmAssign(),
      setAgc: (el) => void this.setAgc((el.dataset['level'] ?? 'high') as AgcLevel),
      runScript: () => void this.runScript(),
      toggleCmd: (el) => {
        const id = Number(el.dataset['id']);
        this.expandedCmd = this.expandedCmd === id ? null : id;
        this.render();
      },
      noop: (_el, ev) => ev.stopPropagation(),
    });
    this.onAct('change', {
      scriptName: (el) => (this.scriptName = (el as HTMLSelectElement).value),
    });
    this.onAct('input', {
      scriptArgs: (el) => (this.scriptArgs = (el as HTMLInputElement).value),
      name: (el) => (this.nameDraft = (el as HTMLInputElement).value),
      deviceId: (el) => (this.deviceIdDraft = (el as HTMLInputElement).value),
      businessSerial: (el) => (this.businessSerialDraft = (el as HTMLInputElement).value),
      deleteText: (el) => {
        this.deleteText = (el as HTMLInputElement).value;
        this.qs<HTMLButtonElement>('[data-act="confirmDelete"]')?.toggleAttribute(
          'disabled',
          this.deleteText.trim() !== (this.device?.name ?? ''),
        );
      },
      forceText: (el) => {
        this.forceText = (el as HTMLInputElement).value;
        this.qs<HTMLButtonElement>('[data-act="confirmForce"]')?.toggleAttribute(
          'disabled',
          this.forceText.trim() !== (this.device?.name ?? ''),
        );
      },
    });
  }

  private async saveIdentity(): Promise<void> {
    if (!this.device) return;
    try {
      this.device = await devices.updateIdentity(this.device.serial, {
        name: this.nameDraft.trim(),
        deviceId: this.deviceIdDraft.trim(),
        businessSerial: this.businessSerialDraft.trim(),
      });
      showToast('Kaydedildi');
      this.render();
    } catch (e) {
      // 409 = bu seri no başka cihazda (filoda tekil olmalı)
      const msg = e instanceof Error && /409|zaten/.test(e.message) ? 'Bu seri no zaten başka bir cihazda' : 'Kaydedilemedi';
      showToast(msg, 'warning');
    }
  }

  private async confirmAndSend(kind: 'restart' | 'rescue'): Promise<void> {
    const d = this.device;
    if (!d) return;
    const spec =
      kind === 'restart'
        ? {
            title: 'Yeniden başlatılsın mı?',
            message: `${d.name} (${d.serial}) yeniden başlatılacak. Oynatma kısa süre kesilir.`,
            tone: 'warning' as const,
            icon: 'ti ti-refresh',
            okLabel: 'Yeniden başlat',
            toast: 'Yeniden başlatma komutu gönderildi',
            toastType: 'info' as const,
          }
        : {
            title: 'Kurtarma moduna alınsın mı?',
            message: `${d.name} (${d.serial}) kurtarma ortamına (RAM-OS) düşürülecek; cihaz yayına ara verir.`,
            tone: 'danger' as const,
            icon: 'ti ti-lifebuoy',
            okLabel: 'Kurtarmaya al',
            toast: 'Kurtarma moduna alma komutu gönderildi',
            toastType: 'warning' as const,
          };
    if (!(await confirmDialog(spec))) return;
    try {
      await devices.command(d.serial, kind);
      showToast(`${d.name} — ${spec.toast} (cihaz anında dürtüldü)`, spec.toastType);
      this.scheduleFollowUp();
    } catch {
      showToast('Komut gönderilemedi', 'warning');
    }
  }

  private async setAgc(level: AgcLevel): Promise<void> {
    const d = this.device;
    if (!d) return;
    try {
      await devices.command(d.serial, 'set_agc', { level });
      showToast(`AGC → ${AGC_LEVELS.find((l) => l.key === level)?.label ?? level} gönderildi (player yeniden başlar)`, 'info');
      this.scheduleFollowUp();
    } catch {
      showToast('AGC komutu gönderilemedi', 'warning');
    }
  }

  private async runScript(): Promise<void> {
    const d = this.device;
    if (!d || !this.scriptName) return;
    const info = this.scripts.find((s) => s.name === this.scriptName);
    const ok = await confirmDialog({
      title: `Script çalıştır: ${this.scriptName}`,
      message: `${info?.description ?? ''}${this.scriptArgs ? `\nArgüman: ${this.scriptArgs}` : ''}\n\nCihazda root olarak, RAM'de çalışır; çıktı bu ekrana düşer.`,
      tone: 'warning',
      icon: 'ti ti-terminal-2',
      okLabel: 'Çalıştır',
    });
    if (!ok) return;
    try {
      await devices.command(d.serial, 'run_script', { name: this.scriptName, args: this.scriptArgs.trim() });
      showToast(`"${this.scriptName}" gönderildi — çıktı gelince aşağıda görünür`, 'info');
      this.scheduleFollowUp();
    } catch {
      showToast('Script gönderilemedi', 'warning');
    }
  }

  /** Komut sonrası birkaç hızlı yenileme: durum değişimi (kurtarma/reboot) hemen görünsün. */
  private scheduleFollowUp(): void {
    for (const ms of [4000, 12000, 25000, 45000, 90000]) {
      setTimeout(() => {
        if (this.isConnected && !this.assignOpen && !this.forceStage && !this.deleteStage) void this.load(true);
      }, ms);
    }
  }

  private async confirmForce(): Promise<void> {
    if (!this.device || this.forceText.trim() !== this.device.name) return;
    try {
      await devices.command(this.device.serial, 'force_install');
      this.forceStage = 'sent';
      this.render();
      this.scheduleFollowUp();
    } catch {
      showToast('Komut gönderilemedi', 'warning');
    }
  }

  private async confirmDelete(): Promise<void> {
    if (!this.device || this.deleteText.trim() !== this.device.name) return;
    try {
      await devices.remove(this.device.serial);
      this.deleteStage = null;
      showToast(`${this.device.name} filodan silindi`);
      navigate('#/devices');
    } catch {
      showToast('Silme başarısız', 'warning');
    }
  }

  private async confirmAssign(): Promise<void> {
    if (!this.device || !this.assignProfile) return;
    try {
      await devices.assign([this.device.serial], this.assignProfile);
      this.assignOpen = false;
      showToast(`"${this.assignProfile}" profili atandı — script kuyruğa alındı`);
      void this.load();
    } catch {
      showToast('Atama başarısız', 'warning');
    }
  }

  /* ---------------- template ---------------- */

  protected override template(): SafeHtml {
    if (this.loading) {
      return html`
        <div class="scr"><div class="body" style="display:flex;align-items:center;justify-content:center">
          <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i>
        </div></div>`;
    }
    const d = this.device;
    if (this.error || !d) {
      return html`
        <div class="scr"><div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
          <div style="text-align:center;color:var(--vx-text-2)">
            <i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
            <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error || 'Cihaz bulunamadı.'}</div>
            <button class="btn sec sm" style="margin-top:12px" data-act="back"><i class="ti ti-chevron-left"></i>Listeye dön</button>
          </div>
        </div></div>`;
    }

    const badge =
      d.status === 'rescue'
        ? { bg: 'var(--vx-danger)', fg: '#fff', icon: 'ti ti-lifebuoy', label: 'Kurtarma' }
        : d.status === 'offline'
          ? { bg: 'var(--vx-chip-bg)', fg: 'var(--vx-chip-fg)', icon: 'ti ti-wifi-off', label: 'Çevrimdışı' }
          : { bg: 'var(--vx-success-bg)', fg: 'var(--vx-success-text)', icon: 'ti ti-point-filled', label: 'Çevrimiçi' };

    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:8px 12px 12px">
          <div style="display:flex;align-items:center;gap:8px">
            <button data-act="back" style="width:38px;height:38px;border-radius:var(--vx-radius-md);border:none;background:none;display:flex;align-items:center;justify-content:center;color:var(--vx-text-2);cursor:pointer">
              <i class="ti ti-chevron-left" style="font-size:22px"></i>
            </button>
            <div style="flex:1;min-width:0">
              <div style="font:600 17px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.name}</div>
              <div class="mono" style="font-size:11px;color:var(--vx-text-3)">${d.businessSerial ? `${d.businessSerial} · ` : ''}${d.serial}${d.deviceId ? ` · ${d.deviceId}` : ''}</div>
            </div>
            ${pendingBadge(d)}
            <span class="chip" style="background:${badge.bg};color:${badge.fg};font-weight:600">
              <i class="${badge.icon}" style="font-size:12px"></i>${badge.label}
            </span>
          </div>
        </div>

        <div class="body" style="padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-content:flex-start;overflow-x:hidden">
          ${when(d.status === 'rescue', () => rescueBadge(d, false))}
          ${this.identityCard(d)}
          ${this.healthCard(d)}
          ${this.profileCard(d)}
          ${this.actionsCard(d)}
          ${this.audioCard(d)}
          ${this.scriptsCard(d)}
        </div>

        ${when(this.sshOpen, () => this.sshPanel(d))}
        ${when(this.assignOpen, () => this.assignDialog(d))}
        ${when(this.forceStage, () => this.forceDialog(d))}
        ${when(this.deleteStage, () => this.deleteDialog(d))}
      </div>`;
  }

  private identityCard(d: Device): SafeHtml {
    const c = session.get().claims;
    const ro = c?.edit ? '' : 'readonly';
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <p class="sect">Kimlik</p>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div><label class="lbl">Cihaz adı</label>
            <input class="inp" data-act="name" value="${this.nameDraft}" ${ro}></div>
          <div style="display:flex;gap:10px;flex-wrap:wrap">
            <div style="flex:1 1 140px"><label class="lbl">Seri no (SMG etiketi)</label>
              <input class="inp mono" data-act="businessSerial" value="${this.businessSerialDraft}" placeholder="elle atanır, ör. SMG-000123" ${ro} title="Bizim seri numaramız (etikete basılan). Filoda tekil olmalı."></div>
            <div style="flex:1 1 140px"><label class="lbl">deviceId (müşteri/entegrasyon)</label>
              <input class="inp mono" data-act="deviceId" value="${this.deviceIdDraft}" placeholder="player tarafı kimlik" ${ro}></div>
          </div>
          <div><label class="lbl">Donanım kimliği (eMMC CID) — değişmez</label>
            <input class="inp mono" value="${d.serial}" readonly title="Cihazın benzersiz donanım kimliği: eMMC yongasının üretici CID'si. Cihaz kendini bununla kaydeder; WireGuard anahtarı ve token buna bağlıdır. Değiştirilemez."></div>
          <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
            <span class="hs" style="color:var(--vx-text-3)"><i class="ti ti-cpu"></i>Amlogic S905X3</span>
            <span class="hs" style="color:var(--vx-text-3)" title="Debian rootfs sürümü"><i class="ti ti-git-branch"></i>${d.version ?? '—'}</span>
            <span class="hs" style="color:var(--vx-text-3)" title="FAT'taki RAM-OS (kurtarma ortamı) sürümü"><i class="ti ti-lifebuoy"></i>RAM-OS ${d.ramosVersion ?? '≤2.9'}</span>
          </div>
          <div style="display:flex;gap:14px;align-items:center;flex-wrap:wrap;padding:10px 12px;background:var(--vx-surface-2);border-radius:var(--vx-radius-md)">
            <span class="hs mono" title="Kablolu iç IP" style="color:${d.net.ethIp ? 'var(--vx-text)' : 'var(--vx-text-3)'}"><i class="ti ti-plug"></i>eth ${d.net.ethIp ?? '—'}</span>
            <span class="hs mono" title="WiFi iç IP" style="color:${d.net.wifiIp ? 'var(--vx-text)' : 'var(--vx-text-3)'}"><i class="ti ti-wifi"></i>wifi ${d.net.wifiIp ?? '—'}</span>
            <span class="hs mono" title="İnternet çıkış IP'si" style="color:${d.net.publicIp ? 'var(--vx-text)' : 'var(--vx-text-3)'}"><i class="ti ti-world"></i>dış ${d.net.publicIp ?? '—'}</span>
            ${when(d.wgIp, () => html`<span class="hs mono" title="WireGuard tünel adresi" style="color:var(--vx-text-3)"><i class="ti ti-lock"></i>wg ${d.wgIp!}</span>`)}
            ${wgModeBadge(d, false)}
          </div>
          <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
            <span style="flex:1"></span>
            ${when(c?.edit, () => html`
              <button class="btn pri sm" style="margin-left:auto" data-act="saveIdentity"><i class="ti ti-check"></i>Kaydet</button>`)}
          </div>
        </div>
      </div>`;
  }

  private healthCard(d: Device): SafeHtml {
    const th = thresholdNumbers();
    const tl = tempLevel(d.health.tempC, th.temp.warn, th.temp.crit);
    const dl = diskLevel(d.health.diskPct, th.disk.warn, th.disk.crit);
    const el = emmcLevel(d.health.emmcLife, th.emmc.warn, th.emmc.crit);
    const history = d.tempHistory ?? [];
    const diskPct = d.health.diskPct ?? 0;
    const emmcPct = Math.min(100, Math.round(((d.health.emmcLife ?? 0) / 12) * 100));

    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <div style="display:flex;align-items:center;margin-bottom:12px">
          <p class="sect" style="margin:0">Sağlık</p>
          <span class="hs" style="margin-left:auto;color:var(--vx-text-3)"><i class="ti ti-clock"></i>son 24 sa</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:14px">
          <div>
            <div style="display:flex;align-items:baseline;gap:7px;margin-bottom:6px">
              <i class="ti ti-temperature" style="font-size:15px;color:${levelColor(tl)}"></i>
              <span style="font:500 12.5px var(--vx-font-sans)">Sıcaklık</span>
              <span class="mono" style="margin-left:auto;font:600 14px var(--vx-font-sans);color:${levelColor(tl)}">
                ${d.health.tempC != null ? `${d.health.tempC}°C` : '—'}
              </span>
            </div>
            ${when(history.length, () => html`
              <div style="display:flex;align-items:flex-end;gap:3px;height:36px">
                ${each(history, (v) => {
                  const lv = tempLevel(v, th.temp.warn, th.temp.crit);
                  const hPct = Math.round((Math.max(30, Math.min(95, v)) / 95) * 100);
                  return html`<span style="flex:1;border-radius:2px 2px 0 0;height:${hPct}%;background:${levelFill(lv)}"></span>`;
                })}
              </div>`)}
          </div>
          <div style="display:flex;gap:18px;flex-wrap:wrap">
            <div style="flex:1 1 120px">
              <div style="display:flex;align-items:baseline;gap:6px;margin-bottom:5px">
                <span style="font:500 12px var(--vx-font-sans)">Kök disk</span>
                <span class="mono" style="margin-left:auto;font:600 13px var(--vx-font-sans);color:${levelColor(dl)}">${d.health.diskPct != null ? `${d.health.diskPct}%` : '—'}</span>
              </div>
              <div style="height:6px;border-radius:999px;background:var(--vx-track)">
                <span style="display:block;height:100%;border-radius:999px;width:${diskPct}%;background:${levelFill(dl)}"></span>
              </div>
            </div>
            <div style="flex:1 1 120px">
              <div style="display:flex;align-items:baseline;gap:6px;margin-bottom:5px">
                <span style="font:500 12px var(--vx-font-sans)">eMMC ömrü</span>
                <span class="mono" style="margin-left:auto;font:600 13px var(--vx-font-sans);color:${levelColor(el)}">${d.health.emmcLife != null ? hex2(d.health.emmcLife) : '—'}</span>
              </div>
              <div style="height:6px;border-radius:999px;background:var(--vx-track)">
                <span style="display:block;height:100%;border-radius:999px;width:${emmcPct}%;background:${levelFill(el)}"></span>
              </div>
            </div>
          </div>
          <div style="display:flex;gap:16px;flex-wrap:wrap">
            <span class="hs" style="color:var(--vx-text-3)"><i class="ti ti-lock"></i>WG el sıkışması ${d.health.wgHandshakeSec != null ? `${d.health.wgHandshakeSec} sn` : '—'}</span>
            <span class="hs" style="color:var(--vx-text-3)"><i class="ti ti-clock-play"></i>uptime ${shortDuration(d.health.uptimeSec)}</span>
          </div>
        </div>
      </div>`;
  }

  private profileCard(d: Device): SafeHtml {
    const c = session.get().claims;
    const profile = this.brandList.find((b) => b.name === d.brand);
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <p class="sect">İmaj profili</p>
        <div style="display:flex;align-items:center;gap:12px">
          <span style="width:40px;height:40px;border-radius:var(--vx-radius-md);background:var(--vx-brand-50);display:flex;align-items:center;justify-content:center;flex:none">
            <i class="ti ti-photo" style="font-size:20px;color:var(--vx-primary)"></i>
          </span>
          <div style="flex:1;min-width:0">
            <div style="font:600 13.5px var(--vx-font-sans)">${d.brand ? `${d.brand} — default imaj` : 'Profil atanmadı'}</div>
            <div class="mono" style="font-size:11px;color:var(--vx-text-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${profile ? `${profile.imageFile} · ${profile.version}` : '—'}
            </div>
          </div>
          ${when(c?.assign, () => html`
            <button class="btn sec sm" data-act="openAssign"><i class="ti ti-repeat"></i>Değiştir</button>`)}
        </div>
      </div>`;
  }

  private actionsCard(d: Device): SafeHtml {
    const c = session.get().claims;
    if (c?.viewer) {
      return html`
        <div class="card" style="flex:1 1 330px;min-width:0">
          <p class="sect">İşlemler</p>
          <div style="display:flex;gap:9px;align-items:flex-start;padding:12px;background:var(--vx-surface-2);border-radius:var(--vx-radius-md)">
            <i class="ti ti-eye" style="color:var(--vx-text-3);font-size:18px;flex:none"></i>
            <span style="font:400 12.5px/1.5 var(--vx-font-sans);color:var(--vx-text-2)">İzleyici rolünde cihaz işlemleri ve SSH erişimi kapalıdır. Yetki için yöneticinle görüş.</span>
          </div>
        </div>`;
    }
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <p class="sect">İşlemler</p>
        <div style="display:flex;flex-direction:column;gap:9px">
          ${when(c?.ssh, () => html`
            <button class="btn sec" style="justify-content:flex-start" data-act="openSSH">
              <i class="ti ti-terminal-2" style="color:var(--vx-text)"></i>SSH terminali aç
              <span style="margin-left:auto;font:500 10px var(--vx-font-sans);color:var(--vx-text-3)">web-ssh</span>
            </button>`)}
          <button class="btn sec" style="justify-content:flex-start" data-act="restart">
            <i class="ti ti-refresh" style="color:var(--vx-text)"></i>Yeniden başlat
          </button>
          <button class="btn sec" style="justify-content:flex-start" data-act="rescue">
            <i class="ti ti-lifebuoy" style="color:var(--vx-text)"></i>Kurtarma moduna al
          </button>
          ${when(c?.force, () => html`
            <button class="btn dngo" style="justify-content:flex-start" data-act="openDelete">
              <i class="ti ti-trash"></i>Cihazı filodan sil
            </button>
            <button class="btn dngo" style="justify-content:flex-start" data-act="openForce">
              <i class="ti ti-reload"></i>Force install — yeniden kur
              <span style="margin-left:auto;font:500 10px var(--vx-font-sans)">tehlikeli</span>
            </button>`)}
        </div>
        ${when(d.status === 'offline', () => html`
          <div style="display:flex;gap:8px;margin-top:12px;padding:10px 12px;background:var(--vx-warning-bg);border-radius:var(--vx-radius-md)">
            <i class="ti ti-wifi-off" style="color:var(--vx-warning);font-size:15px;flex:none"></i>
            <span style="font:400 11.5px/1.5 var(--vx-font-sans);color:var(--vx-warning-text)">Cihaz çevrimdışı — komutlar cihaz tünele döndüğünde işlenir.</span>
          </div>`)}
      </div>`;
  }

  /** Ses / AGC kartı: son set_agc komutundan mevcut seviyeyi türet. */
  private audioCard(d: Device): SafeHtml {
    const c = session.get().claims;
    const lastAgc = this.commandsList.find((x) => x.kind === 'set_agc' && x.status === 'acked');
    const current = String(lastAgc?.payload?.['level'] ?? '');
    const pendingAgc = this.commandsList.find((x) => x.kind === 'set_agc' && x.status !== 'acked');
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <div style="display:flex;align-items:center;margin-bottom:10px">
          <p class="sect" style="margin:0">Ses · AGC (kompresör/limitör)</p>
          ${when(pendingAgc, () => html`<span class="chip" style="margin-left:auto;background:var(--vx-warning-bg);color:var(--vx-warning-text)"><i class="ti ti-loader-2 spin" style="font-size:12px"></i>uygulanıyor</span>`)}
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          ${each(AGC_LEVELS, (l) => html`
            <button class="fchip ${current === l.key ? 'on' : ''}" data-act="setAgc" data-level="${l.key}"
                    ${c?.ops ? '' : 'disabled'} title="${l.hint}">
              ${when(l.key === 'off', () => html`<i class="ti ti-volume-off" style="font-size:14px"></i>`)}
              ${when(l.key !== 'off', () => html`<i class="ti ti-adjustments-horizontal" style="font-size:14px"></i>`)}
              ${l.label}
            </button>`)}
        </div>
        <div style="font:400 11px/1.5 var(--vx-font-sans);color:var(--vx-text-3);margin-top:8px">
          Sistem geneli SC4 (LADSPA) — analog, HDMI, optik ve Bluetooth çıkışlarının hepsinde. Şarkılar arası seviye farkını
          dengeler. ${current ? html`Şu an: <b>${AGC_LEVELS.find((l) => l.key === current)?.label ?? current}</b>.` : 'Varsayılan (imaj): Yüksek.'}
          ${when(d.version === 'v4' || d.version === 'v3', () => html` <span style="color:var(--vx-warning-text)">Bu cihaz ${d.version} — AGC için v5 gerekir.</span>`)}
        </div>
      </div>`;
  }

  /** Script kütüphanesi + komut geçmişi (çıktılar). */
  private scriptsCard(d: Device): SafeHtml {
    const c = session.get().claims;
    const info = this.scripts.find((s) => s.name === this.scriptName);
    return html`
      <div class="card" style="flex:1 1 100%;min-width:0">
        <p class="sect">Script çalıştır · komut geçmişi</p>
        ${when(c?.ops, () => html`
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;margin-bottom:12px">
            <div style="flex:1 1 220px;min-width:0">
              <label class="lbl">Script</label>
              <select class="inp" data-act="scriptName" style="height:42px">
                ${each(this.scripts, (s) => html`<option value="${s.name}" ${s.name === this.scriptName ? 'selected' : ''}>${s.name}</option>`)}
              </select>
            </div>
            <div style="flex:2 1 260px;min-width:0">
              <label class="lbl">Argümanlar ${when(info?.args, () => html`<span style="color:var(--vx-text-3)">· ${info!.args}</span>`)}</label>
              <input class="inp mono" data-act="scriptArgs" value="${this.scriptArgs}" placeholder="${info?.args ?? ''}">
            </div>
            <button class="btn pri" data-act="runScript" ${this.scripts.length ? '' : 'disabled'}><i class="ti ti-player-play"></i>Çalıştır</button>
          </div>
          ${when(info?.description, () => html`
            <div style="font:400 12px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin:-4px 0 12px;padding:9px 11px;background:var(--vx-surface-2);border-radius:var(--vx-radius-md)">
              <i class="ti ti-info-circle" style="font-size:13px;color:var(--vx-info)"></i> ${info!.description}
            </div>`)}`)}
        ${when(!this.commandsList.length, () => html`
          <div style="font:400 12px var(--vx-font-sans);color:var(--vx-text-3)">Henüz komut yok.</div>`)}
        <div style="display:flex;flex-direction:column;gap:2px">
          ${each(this.commandsList.slice(0, 12), (cmd) => this.commandRow(cmd))}
        </div>
      </div>`;
  }

  private commandRow(cmd: DeviceCommand): SafeHtml {
    const label: Record<string, string> = {
      restart: 'Yeniden başlat', rescue: 'Kurtarma modu', force_install: 'Force install',
      run_script: `Script: ${String(cmd.payload?.['name'] ?? '')}`, set_agc: `AGC → ${String(cmd.payload?.['level'] ?? '')}`,
    };
    const st: Record<string, { bg: string; fg: string; t: string }> = {
      pending: { bg: 'var(--vx-chip-bg)', fg: 'var(--vx-chip-fg)', t: 'kuyrukta' },
      delivered: { bg: 'var(--vx-warning-bg)', fg: 'var(--vx-warning-text)', t: 'cihazda' },
      acked: { bg: 'var(--vx-success-bg)', fg: 'var(--vx-success-text)', t: 'tamam' },
      cancelled: { bg: 'var(--vx-chip-bg)', fg: 'var(--vx-text-3)', t: 'iptal' },
    };
    const s = st[cmd.status] ?? st['pending']!;
    const when_ = new Date(cmd.createdSec * 1000).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    const open = this.expandedCmd === cmd.id;
    const exitBad = cmd.result && /\[exit=(?!0\])/.test(cmd.result);
    return html`
      <div style="border-top:1px solid var(--vx-border)">
        <button class="mrow" style="padding:9px 6px;gap:9px" data-act="toggleCmd" data-id="${cmd.id}">
          <span class="mono" style="font-size:11px;color:var(--vx-text-3);flex:none">#${cmd.id}</span>
          <span style="flex:1;min-width:0;font:500 12.5px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${label[cmd.kind] ?? cmd.kind}</span>
          <span class="mono" style="font-size:11px;color:var(--vx-text-3)">${when_}</span>
          <span class="chip" style="background:${exitBad ? 'var(--vx-danger-bg)' : s.bg};color:${exitBad ? 'var(--vx-danger-text)' : s.fg};font-weight:600">${exitBad ? 'hata' : s.t}</span>
          ${when(cmd.result, () => html`<i class="ti ${open ? 'ti-chevron-up' : 'ti-chevron-down'}" style="font-size:15px;color:var(--vx-text-3)"></i>`)}
        </button>
        ${when(open && cmd.result, () => html`
          <pre class="mono" style="margin:0 6px 10px;padding:10px 12px;background:var(--vx-term-bg);color:var(--vx-term-text);border-radius:var(--vx-radius-md);font-size:11.5px;line-height:1.5;white-space:pre-wrap;word-break:break-word;max-height:320px;overflow:auto">${cmd.result}</pre>`)}
      </div>`;
  }

  private sshPanel(d: Device): SafeHtml {
    const url = ssh.termUrl(d.serial);
    // Masaüstü: sürüklenebilir/boyutlanabilir ayrı bileşen (smg-ssh-panel).
    // Mobil: alt-sayfa. İkisi de aynı anda basılır; CSS kırılımı birini gizler.
    return html`
      <smg-ssh-panel class="d-only docked" data-serial="${d.serial}"></smg-ssh-panel>
      <div class="ovl m-only" style="align-items:flex-end" data-act="closeSSH">
        <div class="ssh-sheet term-frame" data-act="noop">
          <div class="term-head">
            <i class="ti ti-terminal-2" style="font-size:18px;color:var(--vx-primary)"></i>
            <span style="font:600 13px var(--vx-font-sans)">web-ssh</span>
            <span class="mono" style="font-size:11px;color:var(--vx-term-text-2)">${d.serial}</span>
            <span class="chip" style="margin-left:auto;background:rgba(30,158,90,.18);color:var(--vx-term-ok);font-weight:600">
              <span style="width:6px;height:6px;border-radius:50%;background:var(--vx-term-ok)"></span>bağlı
            </span>
            <button data-act="closeSSH" style="background:none;border:none;color:var(--vx-term-text-2);cursor:pointer;display:flex">
              <i class="ti ti-x" style="font-size:19px"></i>
            </button>
          </div>
          <iframe class="term-body" src="${url}" title="web-ssh ${d.serial}"></iframe>
        </div>
      </div>`;
  }

  private assignDialog(d: Device): SafeHtml {
    return html`
      <div class="ovl menu" data-act="closeAssign" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop" style="max-height:92%;display:flex;flex-direction:column">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px;flex:none">
            <i class="ti ti-photo-cog" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 17px var(--vx-font-sans)">İmaj profili ata</span>
          </div>
          <div class="body" style="padding:20px">
            <div style="font:400 13.5px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin-bottom:14px">${d.name} cihazına yeni imaj profili atanacak.</div>
            <label class="lbl">Profil</label>
            <div style="display:flex;flex-direction:column;gap:8px">
              ${each(this.brandList, (b) => {
                const active = this.assignProfile === b.name;
                return html`
                  <button data-act="pickAssign" data-brand="${b.name}"
                          style="display:flex;align-items:center;gap:11px;padding:12px;border-radius:var(--vx-radius-md);border:1px solid ${active ? 'var(--vx-primary)' : 'var(--vx-border)'};background:${active ? 'var(--vx-brand-50)' : 'var(--vx-surface)'};cursor:pointer;text-align:left">
                    <span style="width:18px;height:18px;border-radius:50%;border:2px solid ${active ? 'var(--vx-primary)' : 'var(--vx-border-strong)'};display:flex;align-items:center;justify-content:center;flex:none">
                      ${when(active, () => html`<span style="width:9px;height:9px;border-radius:50%;background:var(--vx-primary)"></span>`)}
                    </span>
                    <span style="flex:1;min-width:0">
                      <span style="display:block;font:600 13.5px var(--vx-font-sans)">${b.name} — default</span>
                      <span class="mono" style="font-size:11px;color:var(--vx-text-3)">${b.imageFile} · ${b.version}</span>
                    </span>
                  </button>`;
              })}
            </div>
            <div style="display:flex;gap:8px;margin-top:14px;padding:11px 12px;background:var(--vx-info-bg);border-radius:var(--vx-radius-md)">
              <i class="ti ti-info-circle" style="color:var(--vx-info);font-size:16px;flex:none"></i>
              <span style="font:400 11.5px/1.5 var(--vx-font-sans);color:var(--vx-info-text)">Atama sonrası cihazda imaj indirilir ve smg-player kurulum scripti çalışır.</span>
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--vx-border);flex:none">
            <button class="btn sec" data-act="closeAssign">İptal</button>
            <button class="btn pri" data-act="confirmAssign">Ata ve kur</button>
          </div>
        </div>
      </div>`;
  }

  private forceDialog(d: Device): SafeHtml {
    if (this.forceStage === 'sent') {
      return html`
        <div class="ovl menu" style="animation:fadeIn .15s">
          <div class="sheet wide" data-act="noop">
            <div style="padding:26px 22px;text-align:center">
              <span style="width:54px;height:54px;border-radius:50%;background:var(--vx-success-bg);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
                <i class="ti ti-check" style="font-size:28px;color:var(--vx-success)"></i>
              </span>
              <div style="font:600 17px var(--vx-font-sans);margin-bottom:5px">Komut gönderildi</div>
              <p style="font:400 13px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 18px">
                ${d.name} anında dürtüldü: ~30 sn içinde kurtarma ortamına düşer, ardından marka
                imajı sıfırdan kurulur. İlerlemeyi bu ekranın üstündeki kartta ve listede (%) izle.
              </p>
              <button class="btn pri" style="width:100%" data-act="closeForce">Kapat</button>
            </div>
          </div>
        </div>`;
    }
    return html`
      <div class="ovl menu" data-act="closeForce" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop">
          <div style="padding:20px 20px 0;display:flex;align-items:center;gap:11px">
            <span style="width:40px;height:40px;border-radius:50%;background:var(--vx-danger-bg);display:flex;align-items:center;justify-content:center;flex:none">
              <i class="ti ti-alert-triangle" style="font-size:22px;color:var(--vx-danger)"></i>
            </span>
            <span style="font:600 17px var(--vx-font-sans);color:var(--vx-danger-text)">Yeniden kurulum — force install</span>
          </div>
          <div style="padding:14px 20px 20px">
            <p style="font:400 13.5px/1.6 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 14px">
              Bu işlem ${d.name} cihazını kurtarma moduna alıp markanın default imajını sıfırdan yazar.
              Cihazdaki tüm yerel durum silinir ve işlem <b style="color:var(--vx-danger-text)">geri alınamaz</b>.
            </p>
            <label class="lbl">Onaylamak için yaz: <span class="mono" style="color:var(--vx-text)">${d.name}</span></label>
            <input class="inp" data-act="forceText" placeholder="${d.name}" value="${this.forceText}">
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 20px 20px">
            <button class="btn sec" data-act="closeForce">Vazgeç</button>
            <button class="btn dng" data-act="confirmForce" disabled><i class="ti ti-reload"></i>Yeniden kur</button>
          </div>
        </div>
      </div>`;
  }

  private deleteDialog(d: Device): SafeHtml {
    return html`
      <div class="ovl menu" data-act="closeDelete" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop">
          <div style="padding:20px 20px 0;display:flex;align-items:center;gap:11px">
            <span style="width:40px;height:40px;border-radius:50%;background:var(--vx-danger-bg);display:flex;align-items:center;justify-content:center;flex:none">
              <i class="ti ti-trash" style="font-size:22px;color:var(--vx-danger)"></i>
            </span>
            <span style="font:600 17px var(--vx-font-sans);color:var(--vx-danger-text)">Cihazı filodan sil</span>
          </div>
          <div style="padding:14px 20px 20px">
            <p style="font:400 13.5px/1.6 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 14px">
              ${d.name} kaydı, komut ve sağlık geçmişiyle birlikte silinir; WireGuard erişimi
              anında kesilir. Cihaza DOKUNULMAZ — cihaz hâlâ çalışıyorsa bir sonraki
              temasında kendini <b>yeniden kaydeder</b> (yeni tünel IP'siyle). Bu işlem
              hurdaya ayrılan / iade edilen cihazlar içindir.
            </p>
            <label class="lbl">Onaylamak için yaz: <span class="mono" style="color:var(--vx-text)">${d.name}</span></label>
            <input class="inp" data-act="deleteText" placeholder="${d.name}" value="${this.deleteText}">
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 20px 20px">
            <button class="btn sec" data-act="closeDelete">Vazgeç</button>
            <button class="btn dng" data-act="confirmDelete" disabled><i class="ti ti-trash"></i>Sil</button>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('smg-device-detail', DeviceDetailView);
